import { CalculatorInputs, InvestmentStrategy, TermLength } from './CalculatorInputs';
import { fold, isSome, none, Option, some } from 'fp-ts/lib/Option';
import { pipe } from 'fp-ts/lib/pipeable';
import { CalculatorConfig } from './CalculatorConfig';
import { validTermLengths } from '../shared/constants';
import { AvailableInvestmentStrategies, AvailableStrategy } from './AvailableInvestmentStrategies';

export const VALIDATION_MUST_ENTER_PREMIUM_PAYMENT = 'mustEnterPremiumPayment';
export const VALIDATION_PREMIUM_PAYMENT_OUT_OF_RANGE = 'premiumPaymentOutOfRange';
export const VALIDATION_MUST_CHOOSE_INVESTMENT_TERM = 'mustChooseInvestmentTerm';
export const VALIDATION_INVESTMENT_TERM_LENGTH_NOT_ALLOWED = 'investmentTermLengthNotAllowed';

export const VALIDATION_MUST_SELECT_STRATEGY_TYPES = 'mustSelectStrategyTypes';
export const VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_1 = 'mustSelectStrategyTypeForStrategy1';
export const VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_2 = 'mustSelectStrategyTypeForStrategy2';
export const VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_3 = 'mustSelectStrategyTypeForStrategy3';

export const VALIDATION_MUST_CHOOSE_BUFFERS = 'mustSelectBuffers';
export const VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_1 = 'mustSelectBufferForStrategy1';
export const VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_2 = 'mustSelectBufferForStrategy2';
export const VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_3 = 'mustSelectBufferForStrategy3';

export const VALIDATION_MUST_CHOOSE_INDICES = 'mustSelectIndices';
export const VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_1 = 'mustSelectIndexForStrategy1';
export const VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_2 = 'mustSelectIndexForStrategy2';
export const VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_3 = 'mustSelectIndexForStrategy3';


export const VALIDATION_NO_ALLOCATION_MAY_BE_ZERO = 'noAllocationMayBeZero';
export const VALIDATION_ALLOCATIONS_MUST_SUM_TO_100 = 'allocationsMustSumTo100';
export const VALIDATION_MUST_ENTER_ALLOCATIONS = 'mustEnterAllocations';
export const VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_1 = 'mustEnterAllocationForStrategy1';
export const VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_2 = 'mustEnterAllocationForStrategy2';
export const VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_3 = 'mustEnterAllocationForStrategy3';

export const VALIDATION_STRATEGY_CONFIGURATION_UNKNOWN = 'strategyConfigurationUnknown';
export const VALIDATION_STRATEGIES_MUST_BE_DISTINCT = 'strategiesMustBeDistinct';
export const VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2 = 'strategiesNotDistinct1Matches2';
export const VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3 = 'strategiesNotDistinct1Matches3';
export const VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3 = 'strategiesNotDistinct2Matches3';
export const VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3 = 'strategiesNotDistinct1Matches2Matches3';


const premiumIssues = [VALIDATION_MUST_ENTER_PREMIUM_PAYMENT, VALIDATION_PREMIUM_PAYMENT_OUT_OF_RANGE];
const termLengthIssues = [VALIDATION_MUST_CHOOSE_INVESTMENT_TERM, VALIDATION_INVESTMENT_TERM_LENGTH_NOT_ALLOWED];
const strategyNotSelectedIssues = [
    VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_1,
    VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_2,
    VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_3
];
const bufferNotSelectedIssues = [
    VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_1,
    VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_2,
    VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_3
];
const step4Issues = [
    VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_1,
    VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_2,
    VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_3,
    VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2,
    VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3,
    VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3,
    VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3,
    VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_1,
    VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_2,
    VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_3,
    VALIDATION_NO_ALLOCATION_MAY_BE_ZERO,
    VALIDATION_ALLOCATIONS_MUST_SUM_TO_100
];


export class ValidationFailure {
    readonly issues: Array<string>;
    constructor(issues: Array<string>) {
        this.issues = issues;
    }
    // TODO: we will chain together failures and successes
    isSuccess(): boolean {
        return false;
    }
    getIssues(): Array<string> {
        return [...this.issues];
    }
    hasIssue(issue: string): boolean {
        return this.issues.includes(issue);
    }
    hasIssueIn(issues: Array<string>): boolean {
        for (const i of issues) {
            if (this.issues.includes(i)) {
                return true;
            }
        }
        return false;
    }
}
export class ValidationSuccess {
    isSuccess() {
        return true;
    }
    getIssues(): Array<string> {
        return [];
    }
    hasIssue(_issue: string): boolean {
        return false;
    }
    hasIssueIn(_issues: Array<string>): boolean {
        return false;
    }
}

export type Validation = ValidationSuccess | ValidationFailure;


export function combineValidations(validations: Array<Validation>): Validation {
    const messages = [];
    for (const v of validations) {
        for (const msg of v.getIssues()) {
            messages.push(msg);
        }
    }
    return (messages.length === 0)
        ? new ValidationSuccess()
        : new ValidationFailure(messages);

}

export function combineValidationsOnBlur(oldVal: Validation, newVal: Validation, newValMessages: Array<string>): Validation {
    const messages = [];
    for (const msg of oldVal.getIssues()) {
        if (!newValMessages.includes(msg)) {
            messages.push(msg);
        }
    }
    for (const newMsg of newVal.getIssues()) {
        messages.push(newMsg);
    }

    return (messages.length === 0)
        ? new ValidationSuccess()
        : new ValidationFailure(messages);

}


export function validateStep1PremiumPayment(inputs: CalculatorInputs, config: CalculatorConfig): Validation {
    const failures: Array<string> = [];
    pipe(inputs.premiumPayment,
        fold(() => {
            failures.push(VALIDATION_MUST_ENTER_PREMIUM_PAYMENT);
        },
            (amount) => {
                if (amount < config.minimumPremiumPayment || amount > config.maximumPremiumPayment) {
                    failures.push(VALIDATION_PREMIUM_PAYMENT_OUT_OF_RANGE);
                }
            }));
    return (failures.length > 0)
        ? new ValidationFailure(failures)
        : new ValidationSuccess();
}


export function validateStep1TermLength(inputs: CalculatorInputs, _config: CalculatorConfig): Validation {
    const failures: Array<string> = []
    pipe(inputs.term,
        fold(() => {
            failures.push(VALIDATION_MUST_CHOOSE_INVESTMENT_TERM)
        },
            (term) => {
                if (!validTermLengths.includes(term.years)) {
                    failures.push(VALIDATION_INVESTMENT_TERM_LENGTH_NOT_ALLOWED);
                }
            }))
    return (failures.length > 0)
        ? new ValidationFailure(failures)
        : new ValidationSuccess();
}

export function validateStep1(inputs: CalculatorInputs, config: CalculatorConfig): Validation {
    return combineValidations([
        validateStep1PremiumPayment(inputs, config),
        validateStep1TermLength(inputs, config)
    ]);
}


export function validateStep2(inputs: CalculatorInputs, _config: CalculatorConfig): Validation {
    const failures: Array<string> = [];
    const count = inputs.strategyList.getStrategyCount();
    const specificFailures = [VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_1,
        VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_2,
        VALIDATION_MUST_SELECT_STRATEGY_TYPE_FOR_STRATEGY_3];
    for (let i = 0; i < count; i++) {
        const aStrat = inputs.strategyList.getAllocatedStrategy(i);
        pipe(aStrat.strategy.strategyType,
            fold(() => {
                if (!failures.includes(VALIDATION_MUST_SELECT_STRATEGY_TYPES)) {
                    failures.push(VALIDATION_MUST_SELECT_STRATEGY_TYPES);
                }
                if (i < specificFailures.length) {
                    failures.push(specificFailures[i]);
                }
            },
                (_strategyType) => { return; }));
    }
    return (failures.length > 0)
        ? new ValidationFailure(failures)
        : new ValidationSuccess();
}



export function validateUpToStep2(inputs: CalculatorInputs, config: CalculatorConfig): Validation {
    return combineValidations([
        validateStep1(inputs, config),
        validateStep2(inputs, config)
    ]);
}

export function validateStep3(inputs: CalculatorInputs, _config: CalculatorConfig): Validation {
    const failures: Array<string> = [];
    const count = inputs.strategyList.getStrategyCount();
    const specificFailures = [VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_1,
        VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_2,
        VALIDATION_MUST_CHOOSE_BUFFER_FOR_STRATEGY_3];
    for (let i = 0; i < count; i++) {
        const aStrat = inputs.strategyList.getAllocatedStrategy(i);
        pipe(aStrat.strategy.buffer,
            fold(() => {
                // NOTE: we don't want to show a step3 validation error for a blank strategy row
                // so if strategyType is none, let's not trigger any further issues
                // the UI will just show step2 validation errors
                if (isSome(aStrat.strategy.strategyType)) {
                    if (!failures.includes(VALIDATION_MUST_CHOOSE_BUFFERS)) {
                        failures.push(VALIDATION_MUST_CHOOSE_BUFFERS);
                    }
                    if (i < specificFailures.length) {
                        failures.push(specificFailures[i]);
                    }
                }
            },
                (_strategyType) => { return; }));
    }
    return (failures.length > 0)
        ? new ValidationFailure(failures)
        : new ValidationSuccess();
}

export function validateUpToStep3(inputs: CalculatorInputs, config: CalculatorConfig): Validation {
    return combineValidations([
        validateStep1(inputs, config),
        validateStep2(inputs, config),
        validateStep3(inputs, config)
    ]);
}


export function validateStep4ChooseIndex(inputs: CalculatorInputs, _config: CalculatorConfig): Validation {
    const failures: Array<string> = [];
    const count = inputs.strategyList.getStrategyCount();
    const specificFailures = [VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_1,
        VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_2,
        VALIDATION_MUST_CHOOSE_INDEX_FOR_STRATEGY_3];
    for (let i = 0; i < count; i++) {
        const aStrat = inputs.strategyList.getAllocatedStrategy(i);
        pipe(aStrat.strategy.investmentIndex,
            fold(() => {
                // NOTE: we don't want to show a step3 validation error for a blank strategy row
                // so if strategyType is none, let's not trigger any further issues
                // the UI will just show step2 validation errors
                if (isSome(aStrat.strategy.strategyType)) {
                    if (!failures.includes(VALIDATION_MUST_CHOOSE_INDICES)) {
                        failures.push(VALIDATION_MUST_CHOOSE_INDICES);
                    }
                    if (i < specificFailures.length) {
                        failures.push(specificFailures[i]);
                    }
                }
            },
                (_strategyType) => { return; }));
    }
    return (failures.length > 0)
        ? new ValidationFailure(failures)
        : new ValidationSuccess();
}


export function validateStep4EnterAllocation(inputs: CalculatorInputs, _config: CalculatorConfig): Validation {
    const failures: Array<string> = [];
    const count = inputs.strategyList.getStrategyCount();
    const specificFailures = [VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_1,
        VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_2,
        VALIDATION_MUST_ENTER_ALLOCATION_FOR_STRATEGY_3];
    let totalAllocation = 0;
    for (let i = 0; i < count; i++) {
        const aStrat = inputs.strategyList.getAllocatedStrategy(i);
        pipe(aStrat.allocation,
            fold(() => {
                // NOTE: we don't want to show a step3 validation error for a blank strategy row
                // so if strategyType is none, let's not trigger any further issues
                // the UI will just show step2 validation errors
                if (isSome(aStrat.strategy.strategyType)) {
                    if (!failures.includes(VALIDATION_MUST_ENTER_ALLOCATIONS)) {
                        failures.push(VALIDATION_MUST_ENTER_ALLOCATIONS);
                    }
                    // this will highlight the empty entry
                    if (i < specificFailures.length) {
                        failures.push(specificFailures[i]);
                    }
                }
            },
                (allocation) => {
                    if (allocation === 0) {
                        if (!failures.includes(VALIDATION_NO_ALLOCATION_MAY_BE_ZERO)) {
                            failures.push(VALIDATION_NO_ALLOCATION_MAY_BE_ZERO);
                        }
                        // this will highlight the 0% entry
                        if (i < specificFailures.length) {
                            failures.push(specificFailures[i])
                        }
                    }
                    totalAllocation += allocation;
                }));
    }
    if (totalAllocation !== 100) {
        failures.push(VALIDATION_ALLOCATIONS_MUST_SUM_TO_100);
    }
    return (failures.length > 0)
        ? new ValidationFailure(failures)
        : new ValidationSuccess();
}


export function getStrategyConfig(term: Option<TermLength>, strategy: InvestmentStrategy, available: AvailableInvestmentStrategies): Option<AvailableStrategy> {
    return pipe(term, fold(() => none, (t) =>
        pipe(strategy.strategyType, fold(() => none, (st) =>
            pipe(strategy.buffer, fold(() => none, (b) =>
                pipe(strategy.investmentIndex, fold(() => none, (ii) =>
                    available.strategyForTermAndTypeAndBufferAndIndex(t, st, b, ii)))))))))
}


export function validateStep4DistinctStrategies(inputs: CalculatorInputs, config: CalculatorConfig): Validation {
    const failures: Array<string> = [];
    if (inputs.strategyList.getStrategyCount() === 1) {
        return new ValidationSuccess();
    } else {
        const strategyCodes: Array<string> = [];
        for (let i = 0; i < inputs.strategyList.getStrategyCount(); i++) {
            const availStrat = getStrategyConfig(inputs.term, inputs.strategyList.getAllocatedStrategy(i).strategy, config.availableInvestmentStrategies);
            pipe(availStrat,
                fold(() => {
                    strategyCodes.push(`ukndown<${i}>`);
                    failures.push(VALIDATION_STRATEGY_CONFIGURATION_UNKNOWN);
                },
                    (s) => strategyCodes.push(s.productCode)))
        }
        if (strategyCodes.length === 2) {
            if (strategyCodes[0] === strategyCodes[1]) {
                failures.push(VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2)
            }
        } else if (strategyCodes.length === 3) {
            if (strategyCodes[0] === strategyCodes[1] && strategyCodes[1] === strategyCodes[2]) {
                failures.push(VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2_MATCHES_3)
            } else if (strategyCodes[0] === strategyCodes[1]) {
                failures.push(VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_2)
            } else if (strategyCodes[0] === strategyCodes[2]) {
                failures.push(VALIDATION_STRATEGIES_NOT_DISTINCT_1_MATCHES_3)
            } else if (strategyCodes[1] === strategyCodes[2]) {
                failures.push(VALIDATION_STRATEGIES_NOT_DISTINCT_2_MATCHES_3)
            }
        }
        return (failures.length > 0)
            ? new ValidationFailure(failures)
            : new ValidationSuccess();
    }
}

export function validateStep4(inputs: CalculatorInputs, config: CalculatorConfig): Validation {
    return combineValidations([
        validateStep4ChooseIndex(inputs, config),
        validateStep4EnterAllocation(inputs, config),
        validateStep4DistinctStrategies(inputs, config)
    ]);
}

export function validateUpToStep4(inputs: CalculatorInputs, config: CalculatorConfig): Validation {
    return combineValidations([
        validateStep1(inputs, config),
        validateStep2(inputs, config),
        validateStep3(inputs, config),
        validateStep4(inputs, config)
    ]);
}

function firstIssueOf(targetIssues: string[], allIssues: string[]): Option<string> {
    for (const issue of targetIssues) {
        if (allIssues.includes(issue)) {
            return some(issue);
        }
    }
    return none;
}

export function getFirstValidationError(v: Validation): Option<string> {
    if (v.isSuccess()) {
        return none;
    } else {
        const issues = v.getIssues();
        if (issues.length === 0) {
            return none;
        } else {
            if (v.hasIssueIn(premiumIssues)) {
                return firstIssueOf(premiumIssues, issues);
            } else if (v.hasIssueIn(termLengthIssues)) {
                return firstIssueOf(termLengthIssues, issues);
            } else if (v.hasIssueIn(strategyNotSelectedIssues)) {
                return firstIssueOf(strategyNotSelectedIssues, issues);
            } else if (v.hasIssueIn(bufferNotSelectedIssues)) {
                return firstIssueOf(bufferNotSelectedIssues, issues);
            } else if (v.hasIssueIn(step4Issues)) {
                return firstIssueOf(step4Issues, issues);
            } else {
                return none;
            }
        }
    }
}